import {
    DocumentPaidGradientIcon,
    PayForMediaIcon,
    PayForTextIcon,
    TeaserGoalIcon,
    UploadAudioGoldIcon,
    UploadAudioPurpleIcon,
    UploadDocumentPurpleIcon,
    UploadMediaPurpleIcon
} from '@/assets/icons/icons'
import {useMe} from '@/features/authentication/queries/useMe'
import {getActiveGoalByUserRole} from '@/features/chat/helpers/helpers'
import {GroupParams} from '@/features/chat/types'
import {GoalTeaserModal} from '@/features/goal/components/goal-teaser-modal/GoalTeaserModal'
import {ACCAPTABLE_DOCUMENT_TYPES} from '@/utilities/constants/chat'
import {ACCEPTABLE_AUDIO_TYPES, ACCEPTABLE_FILE_TYPE} from '@/utilities/constants/fileUploader'
import {getChatChannelUserIds, getFileType} from '@/utilities/helpers'
import {ChangeEvent, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {TypePaidMessageE} from '../../AttachmentsPanel/components/SendPaidMessage'
import {SendPaidMessageModal} from '../../AttachmentsPanel/components/SendPaidMessageModal'
import {StyledHostTooltipItem} from './style'
import {FileTypeE, ScopeUploadFileE} from '@/features/chat/services/chat.http'
import {useSendChatFileV3} from '@/features/chat/queries/useSendChatFileV3'
import {useParams} from 'react-router-dom'
import {useSendBroadcastFileV3} from '@/features/chat/queries/useSendBroadcastFileV3'
import {useSendGroupFileV3} from '@/features/chat/queries/useSendGroupFileV3'
import {useGetGoalsForCreator} from '@/features/goal/queries/useGetGoalsForCreator'

export enum HostTooltipSourceE {
    CHAT = 'chat',
    BROADCAST = 'broadcast',
    GROUPS = 'groups'
}
export const HostTooltipContent = ({
    handleSendMessage,
    modalSubtitleName,
    source = HostTooltipSourceE.CHAT,
    sourceData
}: {
    handleSendMessage?: (params: GroupParams) => void
    modalSubtitleName?: string
    source?: HostTooltipSourceE
    sourceData?: {
        usersIds: number[]
        goalsIds: number[]
    }
}) => {
    const {t} = useTranslation()
    const {data: user} = useMe()
    const {data: goalList} = useGetGoalsForCreator()
    const hasHostActiveGoal = !!user && !!getActiveGoalByUserRole(goalList)

    const {channel} = useParams()
    const [guestId, hostId] = useMemo(() => getChatChannelUserIds(`${channel}`), [channel])
    // Goal Teaser Modal
    const [isGoalTeaserModalOpen, setIsGoalTeaserModalOpen] = useState(false)
    // Paid Message Modal
    const [createPaidMediaModalType, setCreatePaidMediaModalType] = useState<TypePaidMessageE | null>(null)
    // Send media
    const {mutate: sendChatFile} = useSendChatFileV3(String(channel))
    const {mutate: sendBroadcastFile} = useSendBroadcastFileV3(sourceData)
    const {mutate: sendGroupFile} = useSendGroupFileV3()

    const onSendFileHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (file) {
            const fileType = getFileType(file.type) ?? ''
            const fileName = `${file?.name}`
            const fileContentType = file?.type || ''
            const onDownloadHandler = {
                [HostTooltipSourceE.CHAT]: sendChatFile,
                [HostTooltipSourceE.BROADCAST]: sendBroadcastFile,
                [HostTooltipSourceE.GROUPS]: sendGroupFile
            }[source]

            !!fileType &&
                onDownloadHandler({
                    payload: {
                        fileName,
                        fileContentType,
                        fileType: fileType as FileTypeE,
                        scope: ScopeUploadFileE.CHAT_ATTACHMENT
                    },
                    file,
                    senderId: +hostId,
                    receiverId: +guestId
                })
        }
    }
    return (
        <div>
            {/* Goal teaser */}
            {hasHostActiveGoal && (
                <StyledHostTooltipItem withBorder onClick={() => setIsGoalTeaserModalOpen(true)}>
                    <TeaserGoalIcon />
                    <span>{t('chat:teaser_tooltip_goal')}</span>
                </StyledHostTooltipItem>
            )}

            {/* paid text */}
            <StyledHostTooltipItem
                withBorder
                onClick={() => {
                    // TODO add correct props for paid text
                    setCreatePaidMediaModalType(TypePaidMessageE.TEXT)
                }}
            >
                <PayForTextIcon />
                <span>{t('chat:pay_for_message:pay_for_text')}</span>
            </StyledHostTooltipItem>

            {/* paid media modal */}
            <StyledHostTooltipItem
                withBorder
                onClick={() => {
                    setCreatePaidMediaModalType(TypePaidMessageE.MEDIA)
                }}
            >
                <PayForMediaIcon height={18} />
                <span>{t('chat:teaser_tooltip_paid_media')}</span>
            </StyledHostTooltipItem>

            <StyledHostTooltipItem
                withBorder
                onClick={() => {
                    // TODO add correct props for paid text
                    setCreatePaidMediaModalType(TypePaidMessageE.AUDIO)
                }}
            >
                <UploadAudioGoldIcon />
                <span>{t('chat:audio')}</span>
            </StyledHostTooltipItem>

            {/* paid document modal */}
            <StyledHostTooltipItem
                withBorder
                onClick={() => {
                    setCreatePaidMediaModalType(TypePaidMessageE.DOCUMENT)
                }}
            >
                <DocumentPaidGradientIcon />
                <span>{t('chat:paid_document')}</span>
            </StyledHostTooltipItem>

            <label>
                <StyledHostTooltipItem>
                    <UploadMediaPurpleIcon height={18} />
                    <span>{t('chat:teaser_tooltip_file')}</span>
                    <input
                        onChange={onSendFileHandler}
                        type="file"
                        style={{display: 'none'}}
                        accept={'.' + ACCEPTABLE_FILE_TYPE.join(', .')}
                    />
                </StyledHostTooltipItem>
            </label>

            <label>
                <StyledHostTooltipItem>
                    <UploadAudioPurpleIcon />
                    <span>{t('chat:audio')}</span>
                    <input
                        onChange={onSendFileHandler}
                        type="file"
                        style={{display: 'none'}}
                        accept={'.' + ACCEPTABLE_AUDIO_TYPES.join(', .')}
                        // disabled={isDisabled}
                    />
                </StyledHostTooltipItem>
            </label>

            <label>
                <StyledHostTooltipItem>
                    <UploadDocumentPurpleIcon />
                    <span>{t('chat:document')}</span>
                    <input
                        onChange={e => onSendFileHandler(e)}
                        type="file"
                        style={{display: 'none'}}
                        accept={'.' + ACCAPTABLE_DOCUMENT_TYPES.join(', .')}
                    />
                </StyledHostTooltipItem>
            </label>

            {isGoalTeaserModalOpen && (
                <GoalTeaserModal
                    isBlockedSending={true}
                    onSendMessage={handleSendMessage}
                    onCloseLocalModal={() => setIsGoalTeaserModalOpen(false)}
                    subtitleName={modalSubtitleName}
                    source={source}
                    sourceData={sourceData}
                />
            )}

            {createPaidMediaModalType && (
                <SendPaidMessageModal
                    onSendMessage={handleSendMessage}
                    onCloseLocalModal={() => setCreatePaidMediaModalType(null)}
                    typeLocalModal={createPaidMediaModalType}
                    source={source}
                    sourceData={sourceData}
                />
            )}
        </div>
    )
}

HostTooltipContent.displayName = 'HostTooltipContent'
